import { useEffect, useRef } from "react";
import { useGoogleMaps } from "./useGoogleMaps";

const createMarkerLabelOverlay = ({ map, position, text }) => {
  const overlay = new window.google.maps.OverlayView();

  overlay.onAdd = function () {
    const div = document.createElement("div");
    div.style.position = "absolute";
    div.style.transform = "translate(-50%, -170%)";
    div.style.background = "#fff";
    div.style.padding = "6px 14px";
    div.style.borderRadius = "8px";
    div.style.boxShadow = "0 4px 10px rgba(0,0,0,0.1)";
    div.style.fontSize = "12px";
    div.style.lineHeight = "16px";
    div.style.maxWidth = "260px";
    div.style.textAlign = "center";
    div.style.whiteSpace = "normal";
    div.innerText = text;

    this.div = div;
    this.getPanes().floatPane.appendChild(div);
  };

  overlay.draw = function () {
    const projection = this.getProjection();
    const pos = projection.fromLatLngToDivPixel(
      new window.google.maps.LatLng(position.lat, position.lng),
    );

    if (this.div) {
      this.div.style.left = pos.x + "px";
      this.div.style.top = pos.y + "px";
    }
  };

  overlay.onRemove = function () {
    if (this.div) this.div.remove();
  };

  overlay.setMap(map);
  return overlay;
};

const LocationMap = ({ lat, lng, locations = [], zoom = 16 }) => {
  const isLoaded = useGoogleMaps();
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markerRefs = useRef([]);
  const overlayRefs = useRef([]);

  useEffect(() => {
    if (!isLoaded) return;

    const resolvedLocations = (
      locations.length
        ? locations
        : lat != null && lng != null
          ? [{ lat, lng }]
          : []
    ).filter((location) => location?.lat != null && location?.lng != null);

    if (!resolvedLocations.length) return;

    const parsedLocations = resolvedLocations.map((location) => ({
      ...location,
      lat: Number(location.lat),
      lng: Number(location.lng),
    }));

    const center = parsedLocations[0];

    if (!mapInstance.current) { 
      mapInstance.current = new window.google.maps.Map(mapRef.current, {
        center,
        zoom,
        scrollwheel: true,
        gestureHandling: "greedy",
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
      });
    }

    markerRefs.current.forEach((marker) => marker.setMap(null));
    overlayRefs.current.forEach((overlay) => overlay.setMap(null));

    markerRefs.current = parsedLocations.map((position, index) => {
      const marker = new window.google.maps.Marker({
        position,
        map: mapInstance.current,
      });

      const overlay = createMarkerLabelOverlay({
        map: mapInstance.current,
        position,
        text: position.address || `Location ${index + 1}`,
      });

      overlayRefs.current[index] = overlay;

      return marker;
    });

    if (parsedLocations.length === 1) {
      mapInstance.current.setCenter(center);
      mapInstance.current.setZoom(zoom);
    } else {
      const bounds = new window.google.maps.LatLngBounds();
      parsedLocations.forEach((position) => bounds.extend(position));
      mapInstance.current.fitBounds(bounds, 80);
    }

    return () => {
      markerRefs.current.forEach((marker) => marker.setMap(null));
      overlayRefs.current.forEach((overlay) => overlay.setMap(null));
      markerRefs.current = [];
      overlayRefs.current = [];
    };
  }, [isLoaded, lat, lng, locations, zoom]);

  return (
    <div className="bg-white rounded-xl p-6 main-shadow">
      <h2 className="text-xl font-semibold text-black mb-6">
        Location
      </h2>

      {!isLoaded ? (
        <div className="h-[300px] bg-gray-100 rounded-lg animate-pulse" />
      ) : (
        <div
          ref={mapRef}
          className="w-full h-[300px] rounded-lg overflow-hidden"
        />
      )}
    </div>
  );
};

export default LocationMap;
